/**
 * PATH: packages/email-hub/src/feature/components/rich-text/tableFormula.ts
 * FILE_ID: EMAILHUB-RICHTEXT-TABLEFORMULA-001
 * PURPOSE: Pure spreadsheet-style formula evaluation for editor tables.
 *          No DOM, no React, no ProseMirror — a single input → output
 *          transformation so the maths is unit-testable in isolation.
 * WHY:     Operators asked for "a table with cells and formula etc."
 *          Emails render as static HTML, so formulas compute WHILE
 *          composing and are baked to fixed values on send. Keeping the
 *          evaluator pure means the live ProseMirror plugin and the
 *          send-time bake share one tested code path.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────┐   ┌──────────────┐
 *   │ "=SUM(A1:A3)"│ → │ parse refs + grid │ → │ number value │
 *   └──────────────┘   └───────────────────┘   └──────────────┘
 * SUPPORTED:
 *   - Full arithmetic expressions: + - * /, parentheses, unary minus and
 *     operator precedence — e.g. =A1*(B1+C1)/2 - 5.
 *   - =FUNC(args)  FUNC ∈ SUM, AVG/AVERAGE, COUNT, MIN, MAX, PRODUCT, MEDIAN
 *     (aggregates), ABS, ROUND (scalar). args = comma-separated expressions,
 *     refs (A1), ranges (A1:B3) or literals (12). Functions may nest and
 *     appear inside larger expressions, e.g. =SUM(A1:A3)/COUNT(A1:A3).
 *   Anything that doesn't parse → { ok:false } so the caller leaves the cell
 *   untouched.
 */

export type FormulaGrid = ReadonlyArray<ReadonlyArray<number | null>>

export type FormulaResult =
  | { ok: true; value: number }
  | { ok: false; error: string }

const AGGREGATES = new Set(['SUM', 'AVG', 'AVERAGE', 'COUNT', 'MIN', 'MAX', 'PRODUCT', 'MEDIAN'])
const SCALAR_FNS = new Set(['ABS', 'ROUND'])

/** Does this trimmed cell text look like a formula? Pure guard. */
export function isFormula(text: string): boolean {
  return typeof text === 'string' && text.trim().startsWith('=') && text.trim().length > 1
}

/** "A" → 0, "B" → 1, "Z" → 25, "AA" → 26 … Returns -1 for invalid. */
export function colLetterToIndex(letters: string): number {
  if (!/^[A-Za-z]+$/.test(letters)) return -1
  let n = 0
  for (const ch of letters.toUpperCase()) {
    n = n * 26 + (ch.charCodeAt(0) - 64)
  }
  return n - 1
}

/** Parse a single ref like "A1" → { row, col } (0-based). null if invalid. */
export function parseRef(ref: string): { row: number; col: number } | null {
  const m = /^([A-Za-z]+)(\d+)$/.exec(ref.trim())
  if (!m) return null
  const col = colLetterToIndex(m[1])
  const row = parseInt(m[2], 10) - 1
  if (col < 0 || row < 0 || !Number.isFinite(row)) return null
  return { row, col }
}

/** Expand "A1:B3" into the list of cell refs it covers (inclusive). */
export function expandRange(range: string): Array<{ row: number; col: number }> {
  const [a, b] = range.split(':')
  const start = parseRef(a)
  const end = b !== undefined ? parseRef(b) : start
  if (!start || !end) return []
  const r0 = Math.min(start.row, end.row)
  const r1 = Math.max(start.row, end.row)
  const c0 = Math.min(start.col, end.col)
  const c1 = Math.max(start.col, end.col)
  const out: Array<{ row: number; col: number }> = []
  // Bound the expansion so a typo like A1:Z9999 can't lock the thread.
  if ((r1 - r0 + 1) * (c1 - c0 + 1) > 4096) return []
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) out.push({ row: r, col: c })
  }
  return out
}

function cellValue(grid: FormulaGrid, row: number, col: number): number | null {
  const r = grid[row]
  if (!r) return null
  const v = r[col]
  return v === undefined ? null : v
}

function median(nums: number[]): number {
  if (nums.length === 0) return 0
  const sorted = [...nums].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function runAggregate(fn: string, nums: number[]): number {
  switch (fn) {
    case 'SUM': return nums.reduce((a, b) => a + b, 0)
    case 'PRODUCT': return nums.length ? nums.reduce((a, b) => a * b, 1) : 0
    case 'COUNT': return nums.length
    case 'AVG':
    case 'AVERAGE': return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0
    case 'MIN': return nums.length ? Math.min(...nums) : 0
    case 'MAX': return nums.length ? Math.max(...nums) : 0
    case 'MEDIAN': return median(nums)
    default: return NaN
  }
}

function runScalar(fn: string, args: number[]): number {
  switch (fn) {
    case 'ABS': return args.length ? Math.abs(args[0]) : NaN
    case 'ROUND': {
      if (args.length === 0) return NaN
      const places = args.length > 1 ? Math.trunc(args[1]) : 0
      const f = 10 ** places
      return Math.round(args[0] * f) / f
    }
    default: return NaN
  }
}

// ── Tokeniser ─────────────────────────────────────────────────────────
type Token =
  | { t: 'num'; v: number }
  | { t: 'ref'; row: number; col: number }
  | { t: 'fn'; name: string }
  | { t: 'op'; v: '+' | '-' | '*' | '/' }
  | { t: 'lpar' }
  | { t: 'rpar' }
  | { t: 'comma' }
  | { t: 'colon' }

class FormulaError extends Error {}

function tokenize(src: string): Token[] {
  const tokens: Token[] = []
  let i = 0
  const n = src.length
  while (i < n) {
    const ch = src[i]
    if (ch === ' ' || ch === '\t' || ch === '\n') { i += 1; continue }
    if (ch === '(') { tokens.push({ t: 'lpar' }); i += 1; continue }
    if (ch === ')') { tokens.push({ t: 'rpar' }); i += 1; continue }
    if (ch === ',') { tokens.push({ t: 'comma' }); i += 1; continue }
    if (ch === ':') { tokens.push({ t: 'colon' }); i += 1; continue }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/') {
      tokens.push({ t: 'op', v: ch }); i += 1; continue
    }
    if (/[0-9.]/.test(ch)) {
      let j = i + 1
      while (j < n && /[0-9.]/.test(src[j])) j += 1
      const num = Number(src.slice(i, j))
      if (!Number.isFinite(num)) throw new FormulaError(`bad number ${src.slice(i, j)}`)
      tokens.push({ t: 'num', v: num })
      i = j
      continue
    }
    if (/[A-Za-z]/.test(ch)) {
      let j = i + 1
      while (j < n && /[A-Za-z]/.test(src[j])) j += 1
      const letters = src.slice(i, j)
      // Letters followed by digits → a cell ref (A1); otherwise a function name.
      if (j < n && /[0-9]/.test(src[j])) {
        let k = j
        while (k < n && /[0-9]/.test(src[k])) k += 1
        const ref = parseRef(letters + src.slice(j, k))
        if (!ref) throw new FormulaError(`bad ref ${letters + src.slice(j, k)}`)
        tokens.push({ t: 'ref', row: ref.row, col: ref.col })
        i = k
      } else {
        tokens.push({ t: 'fn', name: letters.toUpperCase() })
        i = j
      }
      continue
    }
    throw new FormulaError(`unexpected character "${ch}"`)
  }
  return tokens
}

// ── Recursive-descent parser/evaluator ────────────────────────────────
// expr   := add
// add    := mul (('+'|'-') mul)*
// mul    := unary (('*'|'/') unary)*
// unary  := '-' unary | '+' unary | primary
// primary:= num | ref | '(' expr ')' | fn '(' args ')'
class Parser {
  private pos = 0
  private readonly toks: Token[]
  private readonly grid: FormulaGrid

  constructor(toks: Token[], grid: FormulaGrid) {
    this.toks = toks
    this.grid = grid
  }

  evaluate(): number {
    const v = this.parseAdd()
    if (this.pos !== this.toks.length) throw new FormulaError('trailing tokens')
    return v
  }

  private peek(): Token | undefined { return this.toks[this.pos] }
  private next(): Token { const t = this.toks[this.pos]; if (!t) throw new FormulaError('unexpected end'); this.pos += 1; return t }

  private parseAdd(): number {
    let left = this.parseMul()
    for (;;) {
      const t = this.peek()
      if (t?.t === 'op' && (t.v === '+' || t.v === '-')) {
        this.pos += 1
        const right = this.parseMul()
        left = t.v === '+' ? left + right : left - right
      } else break
    }
    return left
  }

  private parseMul(): number {
    let left = this.parseUnary()
    for (;;) {
      const t = this.peek()
      if (t?.t === 'op' && (t.v === '*' || t.v === '/')) {
        this.pos += 1
        const right = this.parseUnary()
        if (t.v === '/') {
          if (right === 0) throw new FormulaError('division by zero')
          left /= right
        } else left *= right
      } else break
    }
    return left
  }

  private parseUnary(): number {
    const t = this.peek()
    if (t?.t === 'op' && t.v === '-') { this.pos += 1; return -this.parseUnary() }
    if (t?.t === 'op' && t.v === '+') { this.pos += 1; return this.parseUnary() }
    return this.parsePrimary()
  }

  private parsePrimary(): number {
    const t = this.next()
    if (t.t === 'num') return t.v
    if (t.t === 'ref') return cellValue(this.grid, t.row, t.col) ?? 0
    if (t.t === 'lpar') {
      const v = this.parseAdd()
      const close = this.next()
      if (close.t !== 'rpar') throw new FormulaError('expected )')
      return v
    }
    if (t.t === 'fn') return this.parseCall(t.name)
    throw new FormulaError('unexpected token')
  }

  private parseCall(name: string): number {
    const open = this.next()
    if (open.t !== 'lpar') throw new FormulaError(`expected ( after ${name}`)
    const nums = this.parseArgs()
    const close = this.next()
    if (close.t !== 'rpar') throw new FormulaError('expected )')
    if (AGGREGATES.has(name)) return runAggregate(name, nums)
    if (SCALAR_FNS.has(name)) return runScalar(name, nums)
    throw new FormulaError(`unknown function ${name}`)
  }

  /** Function args: comma-separated; each is a RANGE (ref:ref → many numbers)
   *  or a full sub-expression (→ one number). Empty arg list is allowed. */
  private parseArgs(): number[] {
    const nums: number[] = []
    if (this.peek()?.t === 'rpar') return nums
    for (;;) {
      // A range only when it's exactly `ref : ref` at this position.
      const a = this.peek()
      const b = this.toks[this.pos + 1]
      if (a?.t === 'ref' && b?.t === 'colon') {
        const start = a
        this.pos += 2
        const end = this.next()
        if (end.t !== 'ref') throw new FormulaError('bad range end')
        for (const cell of rangeCells(start, end)) {
          const v = cellValue(this.grid, cell.row, cell.col)
          if (v !== null) nums.push(v)
        }
      } else {
        nums.push(this.parseAdd())
      }
      if (this.peek()?.t === 'comma') { this.pos += 1; continue }
      break
    }
    return nums
  }
}

function rangeCells(
  a: { row: number; col: number },
  b: { row: number; col: number },
): Array<{ row: number; col: number }> {
  const r0 = Math.min(a.row, b.row)
  const r1 = Math.max(a.row, b.row)
  const c0 = Math.min(a.col, b.col)
  const c1 = Math.max(a.col, b.col)
  // Bound the expansion so a typo like A1:Z9999 can't lock the thread.
  if ((r1 - r0 + 1) * (c1 - c0 + 1) > 4096) throw new FormulaError('range too large')
  const out: Array<{ row: number; col: number }> = []
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) out.push({ row: r, col: c })
  }
  return out
}

/**
 * Evaluate a formula string against a numeric grid.
 * Pure: (formula, grid) → result. Never throws (parse errors → ok:false).
 */
export function computeFormula(formula: string, grid: FormulaGrid): FormulaResult {
  const raw = (formula ?? '').trim()
  if (!raw.startsWith('=')) return { ok: false, error: 'not a formula' }
  const body = raw.slice(1).trim()
  if (body === '') return { ok: false, error: 'empty formula' }
  try {
    const tokens = tokenize(body)
    if (tokens.length === 0) return { ok: false, error: 'empty formula' }
    const value = new Parser(tokens, grid).evaluate()
    return Number.isFinite(value) ? { ok: true, value } : { ok: false, error: 'non-finite result' }
  } catch (err) {
    return { ok: false, error: err instanceof FormulaError ? err.message : 'unsupported formula' }
  }
}

/** Parse a free-form cell string into a number, or null when blank/non-numeric.
 *  Strips currency symbols, thousands separators and whitespace so a column of
 *  "$1,200.50" still sums. */
export function cellTextToNumber(text: string): number | null {
  const cleaned = (text ?? '').replace(/[\s,$£€]/g, '').trim()
  if (cleaned === '') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

/** Format a computed value for display: integers stay integer, decimals
 *  trimmed to at most 4 places (no trailing zeros). Pure. */
export function formatFormulaValue(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return String(Math.round(value * 10000) / 10000)
}
